<h4>What this screen is for</h4>
<p>
    A summary of the book for whatever you have selected in the filter: projected
    revenue and yield against last year, how much of the system's output has been
    left alone, and a set of work queues highlighting journeys worth a look.
    Almost everything here is a starting point &mdash; clicking a bar, point or row
    takes you into the Forecast or Inventory workbench with the filter carried
    across.
</p>

<h4>The cards down the left</h4>
<ul>
    <li><strong>Projected Revenue</strong> &mdash; projected revenue for the selection, with the change against last year.</li>
    <li><strong>Status Report</strong> &mdash; how many records are waiting to be reviewed, split by status. Click a segment to open Inventory filtered to it.</li>
    <li><strong>Price</strong> and <strong>Passengers</strong> &mdash; projected yield and booking volumes, again against last year.</li>
    <li>
        <strong>Forecast Accepted</strong> &mdash; the share of remaining forecast that has
        <em>not</em> been overridden. It measures how much of the model's output has been
        left alone, not how many recommendations someone actively approved.
    </li>
    <li>
        <strong>Inventory Accepted</strong> &mdash; the share of allocation sitting at
        Active or Approved status.
    </li>
</ul>
<p>
    The percentages next to Forecast Accepted and Inventory Accepted are targets
    configured for your operator, not fixed values.
</p>

<h4>Revenue</h4>
<p>
    Booked and projected revenue as columns, this year's and last year's yield as
    lines, and pacing in the lower pane. Series names and colours are configurable
    per operator, so they may not match the wording here exactly.
</p>

<h4>Top 20 Booking Changes</h4>
<p>
    The twenty biggest booking swings over the window chosen in the dropdown
    &mdash; 1, 7 or 14 days, or intraday. All four windows are fetched together, so
    switching between them is instant. The bars are unlabelled; hover for the
    service, journey and departure date.
</p>

<h4>Key Metrics</h4>
<p>
    One panel at a time, chosen from the dropdown:
</p>
<ul>
    <li><strong>Reminder Reports</strong> &mdash; journeys you asked to be reminded about. The arrow icons open Forecast or Inventory; the red cross deletes the reminder after a confirmation.</li>
    <li><strong>Special Events Impact</strong> &mdash; departure dates affected by special events. Hover the counts to see which events and services.</li>
    <li><strong>Top Revenue Opportunities</strong> &mdash; the twenty highest-value journeys still awaiting a decision.</li>
    <li><strong>Override Alerts</strong> &mdash; the overrides that differ most from the system forecast.</li>
    <li><strong>Pacing vs. Remaining Demand</strong> &mdash; journeys pacing furthest from the booking curve, marked according to whether they already carry an inventory override.</li>
    <li><strong>Top Capacity Constraints</strong> &mdash; the tightest legs by forecast share of demand. This one is leg-level; everything else here is journey-level.</li>
    <li><strong>Route Loops</strong> &mdash; services that call at the same station twice.</li>
    <li><strong>Business Rule Coverage</strong> &mdash; the share of journeys covered by at least one applied business rule, broken down by rule type.</li>
    <li><strong>Advanced Purchase Price Alerts</strong> &mdash; advance fares priced above the cheapest flex (walk-up) fare for the same journey, class and journey type while both are on sale. Covers every journey and class with fares on sale today or later, and ignores the sidebar filters. <em>Export to Excel</em> downloads the full list in the current sort order.</li>
</ul>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>For a date range entirely in the past, Projected Revenue shows booked
        revenue instead</strong>, with nothing on screen to say so. Treat the card as
        "revenue" rather than "projection" when looking backwards.
    </li>
    <li>
        <strong>Route Loops ignores the filter.</strong> It shows every route loop
        found, loads the first time you open the panel, and does not reload when you
        search again.
    </li>
    <li>
        On <strong>Special Events Impact</strong>, right-clicking the last column header
        swaps it between remaining demand impact and total remaining demand. There is
        no other way to reach the second column.
    </li>
    <li>
        Drilling in from <strong>Top 20 Booking Changes</strong>, <strong>Top Revenue
        Opportunities</strong> or <strong>Top Capacity Constraints</strong> carries the
        service and date but not the journey, so the destination screen is filtered more
        broadly than the row you clicked.
    </li>
    <li>
        Drill-downs take about half a second to land. There is no spinner, so a click
        can briefly look like it did nothing.
    </li>
</ul>
